import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { ApiClient } from '@up2d8/api-client';
import { LAST_SYNC_KEY, getMeta } from './db';
import { flushOutbox, pendingPostIds } from './outbox';
import { queriesAfterSending } from './outbox-rules';
import { onUpdateAvailable, requestOutboxSync, updateNow } from './register';
import { syncFeed } from './sync';

/**
 * Wat de app over haar eigen verbinding weet (Stap 12).
 *
 * Drie dingen, en ze horen bij elkaar omdat ze samen één verhaal op het scherm vormen: er is geen
 * netwerk, dit is wat je ziet (van wanneer het is), en dit staat nog te wachten.
 *
 * `navigator.onLine` is niet betrouwbaar — een telefoon die op een wifi zit zonder internet, noemt
 * zichzelf online. Daarom is dit een hint voor de weergave en nooit een voorwaarde om iets te
 * proberen: de app doet altijd het verzoek en valt terug op de cache als het mislukt.
 */
interface OfflineState {
  /** Wat de browser zegt. Een hint, geen waarheid. */
  online: boolean;
  /** Wanneer de app voor het laatst met de server sprak. */
  lastSyncedAt: string | null;
  /** Berichten met een bevestiging die nog verstuurd moet worden. */
  pending: Set<string>;
  /** Er staat een nieuwe versie klaar. */
  updateReady: boolean;
  /** Vernieuwt naar de nieuwe versie; herlaadt de app. */
  applyUpdate: () => void;
  /** Probeert de wachtrij te versturen; geeft terug hoeveel er weg zijn. */
  flush: () => Promise<void>;
  /** Opnieuw kijken wat er nog wacht, na een handeling. */
  refreshPending: () => Promise<void>;
}

const OfflineContext = createContext<OfflineState | null>(null);

/**
 * `active` zegt of er een medewerker met een sessie achter dit scherm zit.
 *
 * Alleen dan valt er iets te synchroniseren. Zonder deze grens zou de provider ook op het
 * aanmeldscherm en in de beheerzone om de feed vragen — verzoeken die per definitie mislukken, en die
 * op het aanmeldscherm zelfs een poging tot vernieuwen van de sessie uitlokken.
 */
export function OfflineProvider({
  api,
  active,
  children,
}: {
  api: ApiClient;
  active: boolean;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [online, setOnline] = useState(() => navigator.onLine);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [updateReady, setUpdateReady] = useState(false);

  const refreshPending = useCallback(async () => {
    setPending(await pendingPostIds());
    setLastSyncedAt(await getMeta<string>(LAST_SYNC_KEY));
  }, []);

  const flush = useCallback(async () => {
    const result = await flushOutbox(api);
    await refreshPending();

    // De stand van de medewerker is op de server veranderd; de schermen die daarover gaan, mogen
    // niet op hun oude antwoord blijven staan. Per soort regel: een offline afgevinkt checklist-item
    // ververst de rondes en "Te doen", een bevestiging de feed en het bereik (hertest 29 okt.).
    await Promise.all(
      queriesAfterSending(result.verzondenSoorten).map((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    );
  }, [api, queryClient, refreshPending]);

  /**
   * Bijwerken wat er op het toestel staat, en de wachtrij legen.
   *
   * De orde is met opzet: eerst versturen wat de medewerker deed (dat is zijn werk en het mag niet
   * blijven liggen), dan ophalen wat er veranderd is. Zou het omgekeerd gaan, dan zou een bericht dat
   * intussen is ingetrokken al van het toestel zijn voordat zijn bevestiging eruit was.
   */
  const syncNow = useCallback(async () => {
    await flush();
    try {
      const result = await syncFeed(api);
      if (result.verwijderd > 0 || result.vernieuwd > 0) {
        await queryClient.invalidateQueries({ queryKey: ['feed'] });
      }
    } catch {
      // Geen netwerk, of een sessie die net verlopen is. Wat er staat, blijft staan; dat is het punt
      // van een lokale kopie.
    }
    setLastSyncedAt(await getMeta<string>(LAST_SYNC_KEY));
  }, [api, flush, queryClient]);

  // Bij het openen van de app: kijken wat er nog wacht, versturen, en bijwerken.
  useEffect(() => {
    if (!active) return;
    void refreshPending().then(() => syncNow());
  }, [active, refreshPending, syncNow]);

  useEffect(() => {
    if (!active) return;

    const goOnline = () => {
      setOnline(true);
      void syncNow();
      // En de browser vragen de wachtrij ook te versturen als de app straks dicht is.
      void requestOutboxSync();
    };
    const goOffline = () => setOnline(false);

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    // De service worker vraagt om een ronde als hij door Background Sync gewekt wordt terwijl de app
    // open staat: daar zit de volledige logica.
    const fromWorker = (event: MessageEvent) => {
      if ((event.data as { type?: string } | undefined)?.type === 'FLUSH_OUTBOX') void flush();
    };
    navigator.serviceWorker?.addEventListener('message', fromWorker);

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      navigator.serviceWorker?.removeEventListener('message', fromWorker);
    };
  }, [active, flush, syncNow]);

  useEffect(() => onUpdateAvailable(() => setUpdateReady(true)), []);

  const value = useMemo<OfflineState>(
    () => ({
      online,
      lastSyncedAt,
      pending,
      updateReady,
      applyUpdate: updateNow,
      flush,
      refreshPending,
    }),
    [online, lastSyncedAt, pending, updateReady, flush, refreshPending],
  );

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}

/**
 * De offline-toestand, ook buiten de provider bruikbaar.
 *
 * Zonder provider (de beheerzone gebruikt hem niet) geeft dit een stand die zegt "online, niets te
 * wachten". Zo hoeft geen enkel scherm te weten of het in de medewerkerszone staat.
 */
export function useOffline(): OfflineState {
  return (
    useContext(OfflineContext) ?? {
      online: true,
      lastSyncedAt: null,
      pending: new Set<string>(),
      updateReady: false,
      applyUpdate: () => {},
      flush: async () => {},
      refreshPending: async () => {},
    }
  );
}
